import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, CheckCircle2 } from 'lucide-react'
import { supabase } from '@/config/supabase'
import { ROUTES } from '@/config/routes'
import useBookingStore from '@/stores/bookingStore'
import useAuthStore    from '@/stores/authStore'
import Button from '@/components/ui/Button'

const PROPERTY_TYPES = [
  { val: 'residential_house', label: 'Residential House' },
  { val: 'condo_unit',        label: 'Condo Unit' },
  { val: 'commercial_office', label: 'Commercial Office' },
  { val: 'industrial',        label: 'Industrial' },
]

export default function FinalProposalRequest() {
  const navigate   = useNavigate()
  const profile    = useAuthStore((s) => s.profile)
  const bookingId  = useBookingStore((s) => s.bookingId)
  const quoteId    = useBookingStore((s) => s.quoteId)
  const bookingRef = useBookingStore((s) => s.bookingRef)

  const [quoteRef,      setQuoteRef]      = useState('')
  const [viberNumber,   setViberNumber]   = useState('')
  const [visitDate,     setVisitDate]     = useState('')
  const [propertyType,  setPropertyType]  = useState('')
  const [notes,         setNotes]         = useState('')
  const [submitting,    setSubmitting]    = useState(false)
  const [done,          setDone]          = useState(false)
  const [error,         setError]         = useState('')

  // Fetch quote reference if not in store
  useEffect(() => {
    if (!bookingId) { navigate(ROUTES.BOOK, { replace: true }); return }

    async function fetchRef() {
      if (quoteId) {
        const { data } = await supabase
          .from('quotes')
          .select('quote_reference')
          .eq('id', quoteId)
          .single()
        if (data?.quote_reference) setQuoteRef(data.quote_reference)
      } else if (bookingId) {
        const { data } = await supabase
          .from('quotes')
          .select('id, quote_reference')
          .eq('booking_id', bookingId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single()
        if (data) {
          useBookingStore.getState().setQuoteId(data.id)
          setQuoteRef(data.quote_reference ?? '')
        }
      }
    }
    fetchRef()
  }, [bookingId, quoteId]) // eslint-disable-line

  async function handleSubmit(e) {
    e.preventDefault()
    if (!propertyType) { setError('Please select a property type.'); return }
    setError('')
    setSubmitting(true)

    try {
      const resolvedQuoteId = quoteId || useBookingStore.getState().quoteId

      const { error: insertErr } = await supabase
        .from('final_proposal_requests')
        .insert({
          quote_id:             resolvedQuoteId,
          booking_id:           bookingId,
          customer_id:          profile?.id,
          viber_number:         viberNumber || null,
          preferred_visit_date: visitDate   || null,
          property_type:        propertyType,
          additional_notes:     notes       || null,
          status:               'pending',
        })

      if (insertErr) {
        console.error('FinalProposal insert error:', insertErr)
        setError('Something went wrong. Please try again.')
        setSubmitting(false)
        return
      }

      // Mark quote as proposal_requested
      if (resolvedQuoteId) {
        await supabase
          .from('quotes')
          .update({ review_status: 'approved' })
          .eq('id', resolvedQuoteId)
      }

      setDone(true)
    } catch (err) {
      console.error(err)
      setError('Something went wrong. Please try again.')
    }
    setSubmitting(false)
  }

  // ── Success ──────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-dvh bg-surface-base flex flex-col items-center justify-center px-6 text-center gap-5">
        <div className="w-16 h-16 bg-siglat-teal/15 rounded-2xl flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-siglat-teal" />
        </div>
        <div>
          <h2 className="font-display text-xl font-bold text-ink-primary mb-2">Request Received</h2>
          <p className="text-sm text-ink-muted leading-relaxed">
            Your final proposal will be ready within 3 working days
            (8 hours for urgent requests). Siglat Konstract will be in touch.
          </p>
        </div>
        <Button
          fullWidth
          onClick={() => {
            useBookingStore.getState().reset()
            navigate(ROUTES.BOOK, { replace: true })
          }}
        >
          Back to Home
        </Button>
      </div>
    )
  }

  // ── Form ─────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-dvh bg-surface-base flex flex-col">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top shrink-0">
        <div className="page-container flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-ink-muted hover:text-ink-primary transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-display text-xl font-bold text-ink-primary">Final Proposal Request</h1>
            <p className="text-xs text-ink-muted">Siglat Konstract will prepare your detailed proposal</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="page-container py-5 flex flex-col gap-5">

            {/* Quote reference */}
            <div>
              <label className="block text-xs font-medium text-ink-muted uppercase tracking-widest mb-1.5">
                Quote Reference
              </label>
              <div className="bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5">
                <p className="text-sm font-mono text-siglat-teal">
                  {quoteRef || bookingRef || 'Loading\u2026'}
                </p>
              </div>
            </div>

            {/* Full name (read-only) */}
            <div>
              <label className="block text-xs font-medium text-ink-muted uppercase tracking-widest mb-1.5">
                Full Name
              </label>
              <div className="bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5">
                <p className="text-sm text-ink-primary">{profile?.full_name ?? '\u2014'}</p>
              </div>
            </div>

            {/* Email (read-only) */}
            <div>
              <label className="block text-xs font-medium text-ink-muted uppercase tracking-widest mb-1.5">
                Email Address
              </label>
              <div className="bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5">
                <p className="text-sm text-ink-primary">{profile?.email ?? '\u2014'}</p>
              </div>
            </div>

            {/* Viber number */}
            <div>
              <label className="block text-xs font-medium text-ink-muted uppercase tracking-widest mb-1.5">
                Viber Number <span className="text-ink-muted normal-case font-normal">(optional)</span>
              </label>
              <input
                type="tel"
                value={viberNumber}
                onChange={(e) => setViberNumber(e.target.value)}
                placeholder="+63 9XX XXX XXXX"
                className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-siglat-teal"
              />
            </div>

            {/* Preferred visit date */}
            <div>
              <label className="block text-xs font-medium text-ink-muted uppercase tracking-widest mb-1.5">
                Preferred Site Visit Date <span className="text-ink-muted normal-case font-normal">(optional)</span>
              </label>
              <input
                type="date"
                value={visitDate}
                onChange={(e) => setVisitDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary focus:outline-none focus:ring-1 focus:ring-siglat-teal"
              />
            </div>

            {/* Property type */}
            <div>
              <label className="block text-xs font-medium text-ink-muted uppercase tracking-widest mb-2">
                Property Type <span className="text-siglat-red">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {PROPERTY_TYPES.map((pt) => (
                  <button
                    key={pt.val}
                    type="button"
                    onClick={() => setPropertyType(pt.val)}
                    className={`rounded-xl border px-4 py-3 text-sm font-medium text-left transition-colors
                      ${propertyType === pt.val
                        ? 'bg-siglat-teal/15 border-siglat-teal text-siglat-teal'
                        : 'bg-surface-overlay border-surface-border text-ink-primary hover:border-siglat-teal/50'
                      }`}
                  >
                    {pt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Additional notes */}
            <div>
              <label className="block text-xs font-medium text-ink-muted uppercase tracking-widest mb-1.5">
                Additional Notes <span className="text-ink-muted normal-case font-normal">(optional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any other details Siglat Konstract should know\u2026"
                rows={3}
                className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted resize-none focus:outline-none focus:ring-1 focus:ring-siglat-teal"
              />
            </div>

            {error && (
              <p className="text-sm text-siglat-red">{error}</p>
            )}

            <p className="text-xs text-ink-muted leading-relaxed">
              By submitting, you authorize Siglat Konstract to conduct a site inspection and
              prepare a detailed proposal. No payment is required at this stage.
            </p>

          </div>

          {/* Submit bar */}
          <div className="sticky bottom-0 bg-surface-raised border-t border-surface-border px-4 py-4 safe-bottom">
            <div className="page-container">
              <Button
                type="submit"
                fullWidth
                size="lg"
                loading={submitting}
                disabled={!propertyType}
              >
                Submit Request
              </Button>
            </div>
          </div>

        </form>
      </div>

    </div>
  )
}
